import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  useTheme
} from '@mui/material';
import {
  Upload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Photo as PhotoIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { VenueFormData } from '../../../types/Venue';
import { uploadVenueImage } from '../../../services/venueService';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase/config';

interface VenueImagesFormProps {
  venueData: VenueFormData;
  updateVenueData: (data: Partial<VenueFormData>) => void;
  venueId: string | null; // May be null during initial form filling
}

const IMAGE_PREVIEW_SIZE = 180;

const VenueImagesForm: React.FC<VenueImagesFormProps> = ({ venueData, updateVenueData, venueId }) => {
  const theme = useTheme();
  const [isUploading, setIsUploading] = useState<{ [key: string]: boolean }>({
    primary: false,
    logo: false,
    gallery: false
  });
  const [errors, setErrors] = useState<{ [key: string]: string | null }>({
    primary: null,
    logo: null,
    gallery: null
  });
  
  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, type: 'primary' | 'logo' | 'gallery') => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    // Reset error for this type
    setErrors(prev => ({ ...prev, [type]: null }));
    
    // Check file size (limit to 150MB)
    if (file.size > 150 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, [type]: 'File size exceeds 150MB limit' }));
      return;
    }
    
    // Check file type
    if (!file.type.match('image.*')) {
      setErrors(prev => ({ ...prev, [type]: 'File must be an image' }));
      return;
    }
    
    setIsUploading(prev => ({ ...prev, [type]: true }));
    
    try {
      // Generate a temporary local URL for preview
      const previewUrl = URL.createObjectURL(file);
      
      if (type === 'gallery') {
        // For gallery, we add to the array
        updateVenueData({
          images: {
            ...venueData.images,
            gallery: [...(venueData.images?.gallery || []), previewUrl]
          }
        });
      } else {
        // For primary or logo, we replace
        updateVenueData({
          images: {
            ...venueData.images,
            [type]: previewUrl
          }
        });
      }
      
      // If we have a venueId, upload the image to Firebase
      if (venueId) {
        const timestamp = new Date().getTime();
        const storageRef = ref(storage, `venues/${venueId}/${type}_${timestamp}_${file.name}`);
        
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        
        // Update with the real URL from Firebase
        if (type === 'gallery') {
          const galleryUrls = venueData.images?.gallery?.filter(url => !url.startsWith('blob:')) || [];
          updateVenueData({
            images: {
              ...venueData.images,
              gallery: [...galleryUrls, downloadURL]
            }
          });
        } else {
          updateVenueData({
            images: {
              ...venueData.images,
              [type]: downloadURL
            }
          });
        }
      }
    } catch (err) {
      console.error(`Error uploading ${type} image:`, err);
      setErrors(prev => ({ ...prev, [type]: `Failed to upload image: ${err}` }));
    } finally {
      setIsUploading(prev => ({ ...prev, [type]: false }));
    }
  };
  
  const removeImage = (type: 'primary' | 'logo', isLocal: boolean = false) => {
    updateVenueData({
      images: {
        ...venueData.images,
        [type]: ''
      }
    });
    
    // If the URL is a local blob URL, revoke it to avoid memory leaks
    const imageUrl = venueData.images?.[type];
    if (isLocal && imageUrl && imageUrl.startsWith('blob:')) {
      URL.revokeObjectURL(imageUrl);
    }
  };
  
  const removeGalleryImage = (index: number, isLocal: boolean = false) => {
    const gallery = [...(venueData.images?.gallery || [])];
    
    // If the URL is a local blob URL, revoke it to avoid memory leaks
    if (isLocal && gallery[index].startsWith('blob:')) {
      URL.revokeObjectURL(gallery[index]);
    }
    
    gallery.splice(index, 1);
    updateVenueData({
      images: {
        ...venueData.images,
        gallery
      }
    });
  };
  
  const generateAIImage = async (type: 'primary' | 'logo' | 'gallery') => {
    // This would integrate with your existing Stability AI functionality
    alert('AI image generation will be integrated with your existing Stability AI service');
  };
  
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Venue Images
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Upload high-quality images to showcase your venue. A compelling main image and logo will help attract customers.
      </Typography>
      
      <Grid container spacing={4}>
        {/* Primary Image Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
            Main Image
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            This will be the hero image displayed at the top of your venue page
          </Typography>
          
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: theme.palette.background.default
            }}
          >
            {errors.primary && (
              <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                {errors.primary}
              </Alert>
            )}
            
            {venueData.images?.primary ? (
              <Box sx={{ position: 'relative', width: '100%', mb: 2 }}>
                <Box
                  component="img"
                  src={venueData.images.primary}
                  alt="Primary Venue Image"
                  sx={{
                    width: '100%',
                    height: 200,
                    objectFit: 'cover',
                    borderRadius: 1,
                  }}
                />
                <IconButton
                  size="small"
                  color="error"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(255,255,255,0.8)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                  }}
                  onClick={() => removeImage('primary', true)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: 200,
                  border: `2px dashed ${theme.palette.divider}`,
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <ImageIcon sx={{ fontSize: 60, color: theme.palette.text.disabled }} />
              </Box>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <Button
                variant="contained"
                component="label"
                disabled={isUploading.primary}
                startIcon={isUploading.primary ? <CircularProgress size={20} /> : <UploadIcon />}
                sx={{ flexGrow: 1 }}
              >
                {isUploading.primary ? 'Uploading...' : 'Upload Image'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'primary')}
                />
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => generateAIImage('primary')}
                sx={{ flexGrow: 1 }}
              >
                Generate AI Image
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        {/* Logo Section */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
            Venue Logo
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Your venue's logo will appear in search results and at the top of your page
          </Typography>
          
          <Paper
            elevation={2}
            sx={{
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              bgcolor: theme.palette.background.default
            }}
          >
            {errors.logo && (
              <Alert severity="error" sx={{ mb: 2, width: '100%' }}>
                {errors.logo}
              </Alert>
            )}
            
            {venueData.images?.logo ? (
              <Box sx={{ position: 'relative', width: '100%', mb: 2, display: 'flex', justifyContent: 'center' }}>
                <Box
                  component="img"
                  src={venueData.images.logo}
                  alt="Venue Logo"
                  sx={{
                    width: 120,
                    height: 120,
                    objectFit: 'contain',
                    borderRadius: 1,
                  }}
                />
                <IconButton
                  size="small"
                  color="error"
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    bgcolor: 'rgba(255,255,255,0.8)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                  }}
                  onClick={() => removeImage('logo', true)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ) : (
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  border: `2px dashed ${theme.palette.divider}`,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <PhotoIcon sx={{ fontSize: 40, color: theme.palette.text.disabled }} />
              </Box>
            )}
            
            <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
              <Button
                variant="contained"
                component="label"
                disabled={isUploading.logo}
                startIcon={isUploading.logo ? <CircularProgress size={20} /> : <UploadIcon />}
                sx={{ flexGrow: 1 }}
              >
                {isUploading.logo ? 'Uploading...' : 'Upload Logo'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'logo')}
                />
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => generateAIImage('logo')}
                sx={{ flexGrow: 1 }}
              >
                Generate AI Logo
              </Button>
            </Box>
          </Paper>
        </Grid>
        
        {/* Gallery Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium' }}>
            Image Gallery
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Add additional images to showcase different aspects of your venue
          </Typography>
          
          <Paper
            elevation={2}
            sx={{
              p: 2,
              bgcolor: theme.palette.background.default
            }}
          >
            {errors.gallery && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.gallery}
              </Alert>
            )}
            
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {venueData.images?.gallery && venueData.images.gallery.map((image, index) => (
                <Grid item key={index}>
                  <Card sx={{ width: IMAGE_PREVIEW_SIZE, position: 'relative' }}>
                    <CardMedia
                      component="img"
                      height={IMAGE_PREVIEW_SIZE}
                      image={image}
                      alt={`Gallery image ${index + 1}`}
                    />
                    <IconButton
                      size="small"
                      color="error"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        bgcolor: 'rgba(255,255,255,0.8)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.95)' },
                      }}
                      onClick={() => removeGalleryImage(index, true)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Card>
                </Grid>
              ))}
              
              {/* Empty placeholder */}
              {(!venueData.images?.gallery || venueData.images.gallery.length === 0) && (
                <Grid item xs={12}>
                  <Box
                    sx={{
                      height: 200,
                      border: `2px dashed ${theme.palette.divider}`,
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant="body2" color="text.disabled">
                      No gallery images added yet
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                component="label"
                disabled={isUploading.gallery}
                startIcon={isUploading.gallery ? <CircularProgress size={20} /> : <UploadIcon />}
              >
                {isUploading.gallery ? 'Uploading...' : 'Add Gallery Image'}
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'gallery')}
                />
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<AutoAwesomeIcon />}
                onClick={() => generateAIImage('gallery')}
              >
                Generate AI Gallery Image
              </Button>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default VenueImagesForm;
